import {type FC, useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useEventGiftingClassifiedCategory} from '~/CreateFabricInvitation/routes/EventGifting/useEventGiftingClassifiedCategory';
import type {GiftOptionProps} from '~/CreateFabricInvitation/types/types';
import {FEATURE_QA_IDS} from '~/CreateFabricInvitation/utils/constants';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {
  selectCharityBeneficiaryName,
  selectCharityID,
  selectCrowdfundPurpose,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {EventFeature} from '~/common/types/event';
import {HeartCircleIcon} from '~library/svg/HeartCircleIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';
import {DonationOverlay} from './DonationOverlay';

export const DonationOption: FC<GiftOptionProps> = ({
  hideIfInactive = false,
  hideIfActive = false,
  renderAs = 'block',
  controllerOpen = false,
  onToggleController,
  noOverlay = false,
}) => {
  const [showOverlay, setShowOverlay] = useState(controllerOpen);
  const charityId = useSelector(selectCharityID);
  const crowdfundPurpose = useSelector(selectCrowdfundPurpose);
  const charityBeneficiaryName = useSelector(selectCharityBeneficiaryName);
  const {isWeddingEvent, isBabyEvent, isFuneralEvent} = useEventGiftingClassifiedCategory();

  useEffect(() => {
    setShowOverlay(controllerOpen);
  }, [controllerOpen]);

  const closeOverlay = useCallback(() => {
    if (onToggleController) onToggleController();
    else setShowOverlay(false);
  }, [onToggleController]);

  const onClickAddOrEdit = useCallback(() => {
    if (charityId || crowdfundPurpose) {
      scrollElementIntoViewById('module-donation', 'start', 0);
    } else {
      window.scrollTo(0, document.body.scrollHeight);
    }
    if (onToggleController) onToggleController();
    else setShowOverlay(true);
  }, [charityId, crowdfundPurpose, onToggleController]);

  let title = 'Support a cause you care about';

  if (isFuneralEvent) {
    title = 'Add a charity in their memory';
  }

  if (isWeddingEvent || isBabyEvent) {
    title = 'Support a cause you care about';
  }

  const hide = Boolean((hideIfInactive && !charityId) || (hideIfActive && charityId));

  return (
    <InvitationWidget.Toggle
      editable
      renderAs={renderAs}
      title={renderAs === 'pill' ? 'Charity' : title}
      subtitle={charityBeneficiaryName}
      filled={!!charityId}
      hide={hide}
      onClick={onClickAddOrEdit}
      startAdornment={<HeartCircleIcon ratio={0.75} />}
      qaId={FEATURE_QA_IDS[EventFeature.CHARITY] as string}
      overlay={
        noOverlay ? null : <DonationOverlay isOpen={showOverlay} closeOverlay={closeOverlay} />
      }
    />
  );
};
